import { PrintMenu } from '../print-menu';
import { Type } from '@angular/core';
import { MenuHeaderTitleImageComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/menu-header-title/menu-header-title-image/menu-header-title-image.component';
import { PrintHeaderLayoutType } from '../../enum/shared/print-header-layout-type.enum';
import { SectionColumnType } from '../../enum/shared/section-column-type';
import { SectionColumnProductInfoType } from '../../enum/shared/section-column-product-info-type';
import { SectionColumnViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';
import { Section } from '../section/section';
import { SectionRowViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import { StrainType } from '../../enum/dto/strain-classification.enum';
import { SortVariantUtils } from '../../../utils/sort-variant-utils';
import { StringUtils } from '../../../utils/string-utils';
import { ColumnGrouping } from '../section/column-grouping';
import { ColumnUtils } from '../../../utils/column-utils';
import { FONT, FontStyleSheet } from '../font-style-sheets';
import { PrintFooterLayoutType } from '../../enum/shared/print-footer-layout-type.enum';
import { ProductMenuFooterComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-footer/product-menu-footer.component';
import { WithinNameCell } from '../../enum/shared/within-name-cell';
import { exists } from '../../../functions/exists';
import { SpiritLeafPrintFooterComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-footer/spirit-leaf-print-footer/spirit-leaf-print-footer.component';

export class SpiritLeafPrintMenu extends PrintMenu {
  getPageTopMargin(): string {
    return '0.25in';
  }

  getPageBottomMargin(): string {
    return '0.25in';
  }

  getPageLeftMargin(): string {
    return '0.25in';
  }

  getPageRightMargin(): string {
    return '0.25in';
  }

  override getTitleSectionTopMargin(): string {
    return '0';
  }

  override getTitleSectionBottomMargin(): string {
    return '0';
  }

  override getThemeClass(): string {
    return 'spirit-leaf-print-theme';
  }

  override getHeaderType(): Type<MenuHeaderTitleImageComponent> {
    return MenuHeaderTitleImageComponent;
  }

  override getPrintHeaderLayoutType(): PrintHeaderLayoutType {
    return this.menuOptions?.printHeaderLayout || PrintHeaderLayoutType.FirstPage;
  }

  override getShowInlineLabels(): WithinNameCell {
    return WithinNameCell.TitleLine;
  }

  override fixedLocationBrandPropertyIsWithin(): WithinNameCell | null {
    return WithinNameCell.SubtitleLine;
  }

  override fixedLocationLineagePropertyIsWithin(): WithinNameCell | null {
    return WithinNameCell.SubtitleLine;
  }

  override getColumnOrdering(): Map<SectionColumnType, number> {
    return new Map<SectionColumnType, number>([
      [SectionColumnProductInfoType.StrainType, 1],
      [SectionColumnProductInfoType.ProductTitle, 2],
      [SectionColumnProductInfoType.Brand, 3],
      [SectionColumnProductInfoType.Badge, 4],
      [SectionColumnProductInfoType.Strain, 5],
      ...SectionColumnViewModel.getDefaultAdditionalDetailsColumnOrder(6),
      SectionColumnViewModel.getDefaultProductTagGroupColumnOrdering(7),
      ...SectionColumnViewModel.getDefaultCannabinoidColumnOrdering(8),
      ...SectionColumnViewModel.getDefaultTerpeneColumnOrdering(9),
      [SectionColumnProductInfoType.QuantityAndSize, 10],
      [SectionColumnProductInfoType.Quantity, 11],
      [SectionColumnProductInfoType.Size, 12],
      [SectionColumnProductInfoType.VariantType, 13],
      [SectionColumnProductInfoType.Stock, 14],
      [SectionColumnProductInfoType.GradingResults, 15],
      [SectionColumnProductInfoType.VariantPrice, 16],
      [SectionColumnProductInfoType.VariantSecondaryPrice, 17]
    ]);
  }

  /**
   * TODO - KFFT
   * I might have to rethink this solution when
   * - [Admin] Theme Defaults: add “Fixed Location (visibility-only)” toggle per column option
   * - https://budsense.atlassian.net/browse/BUD-10342
   * is added, because this way doesn't automatically plugin
   */
  override getShouldInflateColumnHeaderForRow(
    s: Section,
    col: SectionColumnViewModel,
    rows: SectionRowViewModel[]
  ): boolean {
    const classification = col?.columnType === SectionColumnProductInfoType.StrainType;
    const strain = col?.columnType === SectionColumnProductInfoType.Strain;
    return !classification && !strain;
  }

  /**
   * TODO - KFFT
   * I might have to rethink this solution when
   * - [Admin] Theme Defaults: add “Fixed Location (visibility-only)” toggle per column option
   * - https://budsense.atlassian.net/browse/BUD-10342
   * is added, because this way doesn't automatically plugin
   */
  override getShouldInflateColumnForRow(s: Section, col: SectionColumnViewModel, row: SectionRowViewModel): boolean {
    const strain = col?.columnType === SectionColumnProductInfoType.Strain;
    if (exists(strain)) {
      return false;
    }
    const badge = col?.columnType === SectionColumnProductInfoType.Badge;
    return !badge || (badge && exists(row?.hasBadges()));
  }

  override getUniqueClassificationList(): StrainType[] {
    return super
      .getUniqueClassificationList()
      ?.filter(s => s !== StrainType.SativaDominant && s !== StrainType.IndicaDominant)
      ?.sort(SortVariantUtils.genericStrainTypeSort);
  }

  override getPriceColumnTitle(col: SectionColumnViewModel): string {
    return StringUtils.removeWhiteSpace(super.getPriceColumnTitle(col));
  }

  override getLineModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  override getGridModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  override getSectionHeaderUnderlined(): boolean {
    return false;
  }

  override whatFontsAreNeededToRenderContent(): FontStyleSheet[] {
    return [
      FONT.Calluna.SemiBold_600,
      FONT.Montserrat.Light_300,
      FONT.Montserrat.Medium_500,
      FONT.Poppins.Bold_700,
      FONT.PtMono.Bold_700
    ];
  }

  override getAdobeFontFamilyCssUrl(): string | null {
    return 'https://use.typekit.net/lrv2ebx.css';
  }

  override getPrintFooterLayoutType(): PrintFooterLayoutType {
    return this.menuOptions?.printFooterLayout || PrintFooterLayoutType.AllPagesFixed;
  }

  override getFooterType(): Type<ProductMenuFooterComponent> {
    return SpiritLeafPrintFooterComponent;
  }
}
